import React, { useState, useEffect, useRef } from 'react';

const AutoCompleteMobile = ({
    name,
    label,
    suggestions = [],
    fetchSuggestions, // Optional async function
    minChars = 5,
    value,
    onChange,
    onBlur,
    error,
    touched,
    displayField = 'name', // Field to display in suggestions
    valueField = 'id', // Field to use as value
    ...props
}) => {
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(0);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);

    // Find display value for the current input value
    const getDisplayValue = (inputValue) => {
        if (!inputValue) return '';

        // Try to find matching suggestion for the current value
        const matchedSuggestion = suggestions.find(suggestion =>
            suggestion[valueField] === inputValue ||
            suggestion[displayField] === inputValue
        );

        return matchedSuggestion ? matchedSuggestion[displayField] : inputValue;
    };

    useEffect(() => {
        const filterSuggestions = async () => {
            if (value?.length < minChars) {
                setFilteredSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            if (fetchSuggestions) {
                // Async suggestions from API
                setLoading(true);
                try {
                    const results = await fetchSuggestions(value);
                    setFilteredSuggestions(results);
                    setShowSuggestions(results.length > 0);
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                    setFilteredSuggestions([]);
                } finally {
                    setLoading(false);
                }
            } else {
                // Local suggestions filtering
                const filtered = suggestions.filter(suggestion => {
                    const displayText = suggestion[displayField]?.toString() || '';
                    return displayText.includes(value);
                });
                setFilteredSuggestions(filtered);
                setShowSuggestions(filtered.length > 0);
            }
        };

        const timeoutId = setTimeout(() => {
            filterSuggestions();
        }, 300); // Debounce delay

        return () => clearTimeout(timeoutId);
    }, [value, suggestions, fetchSuggestions, minChars, displayField]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
                if (onBlur) onBlur(name, true);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [name, onBlur]);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        onChange(name, newValue);
        setActiveSuggestion(0);
    };

    const handleSuggestionClick = (suggestion) => {
        // Pass both the object and the value field
        onChange(name, suggestion);
        if (onBlur) onBlur(name, true);
        setShowSuggestions(false);
    };

    const handleKeyDown = (e) => {
        if (showSuggestions) {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (filteredSuggestions[activeSuggestion]) {
                    onChange(name, filteredSuggestions[activeSuggestion]);
                    setShowSuggestions(false);
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (activeSuggestion > 0) {
                    setActiveSuggestion(activeSuggestion - 1);
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (activeSuggestion < filteredSuggestions.length - 1) {
                    setActiveSuggestion(activeSuggestion + 1);
                }
            } else if (e.key === 'Escape') {
                setShowSuggestions(false);
            }
        }
    };

    const handleBlur = (e) => {
        if (onBlur) onBlur(name, true);
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200); // Small delay to allow click events
    };

    return (
        <div className="formik-autocomplete-wrapper" ref={wrapperRef}>
            {label && (
                <label htmlFor={name} className="form-label">
                    {label}
                </label>
            )}

            <div className="input-wrapper">
                <input
                    {...props}
                    id={name}
                    name={name}
                    value={getDisplayValue(value) || ''}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={() => value?.length >= minChars && setShowSuggestions(true)}
                    autoComplete="off"
                    className={`form-control ${touched && error ? 'is-invalid' : ''}`}
                />

                {loading && (
                    <div className="suggestions-loading">
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                        <span className="ms-2">Loading...</span>
                    </div>
                )}

                {showSuggestions && filteredSuggestions.length > 0 && (
                    <ul className="suggestions-list">
                        {filteredSuggestions.map((suggestion, index) => (
                            <li
                                key={`suggestionItem_${index}`}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className={`suggestion-item ${index === activeSuggestion ? 'active' : ''
                                    }`}
                            >
                                {suggestion[displayField]}
                            </li>
                        ))}
                    </ul>
                )}

                {showSuggestions && !loading && filteredSuggestions.length === 0 && value.length >= minChars && (
                    <div className="no-suggestions">
                        <em>No suggestions found</em>
                    </div>
                )}
            </div>

            {touched && error && (
                <div className="invalid-feedback">{error}</div>
            )}
        </div>
    );
};

export default AutoCompleteMobile;